class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    /* Catedo 27.08.2026 — fluidité : une seule requête /cart/change à la
       fois. File « dernière intention par article » : les clics rapides se
       remplacent au lieu de s'empiler, et une réponse ancienne ne peut pas
       écraser une action plus récente (requêtes sérialisées).
       File et verrou PARTAGÉS entre toutes les instances : le re-rendu du
       tiroir recrée l'élément cart-drawer-items, une file par instance
       perdrait la sérialisation entre l'ancienne et la nouvelle. */
    if (!CartItems.pendingActions) CartItems.pendingActions = new Map();
    if (typeof CartItems.changeInFlight === 'undefined') CartItems.changeInFlight = false;

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    /* On transmet l'input source : même si le rendu a été remplacé entre
       la saisie et la fin du debounce, il porte encore la clé de ligne
       Shopify qui permet de cibler la bonne ligne. */
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target);
  }

  onCartUpdate() {
    fetch('/cart?section_id=main-cart-items')
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  /* API publique inchangée (cart-remove-button, quantity input) ;
     sourceInput est optionnel (fourni par onChange). */
  updateQuantity(line, quantity, name, sourceInput) {
    /* Jamais de quantité négative ni invalide. */
    let parsedQuantity = parseInt(quantity, 10);
    if (isNaN(parsedQuantity) || parsedQuantity < 0) parsedQuantity = 0;

    const row = this.querySelector(`#CartDrawer-Item-${line}`) || this.querySelector(`#CartItem-${line}`);
    if (parsedQuantity === 0) {
      /* Double clic sur « Supprimer » : la première demande suffit. */
      if (row && row.classList.contains('cart-item--removing')) return;
      /* Repli visuel réservé au tiroir : la page /cart garde son
         comportement d'origine (hors périmètre). */
      if (row && row.closest('cart-drawer')) this.collapseRow(row);
    }

    /* Identification de la ligne : d'abord l'input source de l'événement
       (fiable même détaché après un remplacement de rendu), sinon
       recherche par index dans le document. */
    const quantityElement = (sourceInput && sourceInput.dataset) ? sourceInput
      : (document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`));
    const variantId = quantityElement && quantityElement.dataset ? quantityElement.dataset.quantityVariantId : null;
    /* Clé de ligne Shopify (item.key) : unique par ligne, y compris quand
       une même variante apparaît plusieurs fois avec des propriétés
       différentes. Repli : identifiant de variante, puis index. */
    const lineKey = quantityElement && quantityElement.dataset ? (quantityElement.dataset.lineKey || null) : null;

    this.setAmountsUpdating(true);
    CartItems.pendingActions.set(lineKey || variantId || `line-${line}`, { line, lineKey, variantId, quantity: parsedQuantity, name });
    this.flushPendingActions();
  }

  /* Réaction visuelle immédiate à la suppression : la ligne se replie.
     Rien n'est réellement retiré : l'état reste restaurable en cas d'erreur. */
  collapseRow(row) {
    row.style.setProperty('--catedo-row-h', `${row.offsetHeight}px`);
    row.classList.add('cart-item--removing');
    row.setAttribute('aria-hidden', 'true');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => row.classList.add('cart-item--collapsed'));
    });
  }

  restoreRow(line) {
    /* Résolution dans le document vivant : l'instance qui traite la
       réponse peut avoir été détachée par un remplacement de rendu. */
    const row = document.getElementById(`CartDrawer-Item-${line}`) || document.getElementById(`CartItem-${line}`);
    if (!row) return;
    row.classList.remove('cart-item--removing', 'cart-item--collapsed');
    row.style.removeProperty('--catedo-row-h');
    row.removeAttribute('aria-hidden');
    const removeButton = row.querySelector('cart-remove-button button');
    if (removeButton) removeButton.focus();
  }

  /* Montants réels marqués « en cours de mise à jour » (pulsation CSS) :
     on n'invente jamais le nouveau total. */
  setAmountsUpdating(on) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('catedo-amounts-updating', on);
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('catedo-amounts-updating', on);
  }

  flushPendingActions() {
    if (CartItems.changeInFlight) return;
    const next = CartItems.pendingActions.entries().next();
    if (next.done) {
      this.setAmountsUpdating(false);
      return;
    }
    const [key, action] = next.value;
    CartItems.pendingActions.delete(key);

    /* Les index de ligne changent après chaque suppression : on retrouve
       la ligne actuelle via sa clé de ligne Shopify (item.key, unique même
       quand une variante occupe plusieurs lignes), à défaut via son
       identifiant de variante. Résolution dans le DOCUMENT vivant : le
       re-rendu du tiroir recrée cart-drawer-items, et l'instance qui
       exécute ce flush peut être l'ancienne, détachée — son propre arbre
       contient des index périmés. Une clé connue mais absente du rendu =
       ligne disparue : on n'utilise JAMAIS le repli variante dans ce cas,
       il pourrait cibler une autre ligne de la même variante. */
    let line = action.line;
    if (action.lineKey || action.variantId) {
      const input = action.lineKey
        ? document.querySelector(`.quantity__input[data-line-key="${action.lineKey}"]`)
        : document.querySelector(`.quantity__input[data-quantity-variant-id="${action.variantId}"]`);
      if (input && input.dataset.index) {
        line = input.dataset.index;
        /* Action déjà satisfaite par le serveur (valeur rendue identique) :
           inutile d'envoyer une requête redondante. */
        if (action.quantity > 0 && parseInt(input.getAttribute('value'), 10) === action.quantity) {
          input.value = action.quantity;
          this.flushPendingActions();
          return;
        }
      } else {
        /* La ligne n'est plus dans le panier : action obsolète. */
        this.flushPendingActions();
        return;
      }
    }
    CartItems.changeInFlight = true;
    this.sendCartChange(line, action.quantity, action.name);
  }

  sendCartChange(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text().then((state) => ({ ok: response.ok, state }));
      })
      .then(({ ok, state }) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors || !ok) {
          /* Restauration complète : quantité affichée, ligne repliée,
             message d'erreur accessible. */
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          if (quantity === 0) this.restoreRow(line);
          this.updateLiveRegions(line, parsedState.errors || parsedState.description || window.cartStrings.error);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        /* Une intention utilisateur pas encore envoyée (saisie plus récente
           que cette réponse) ne doit pas être écrasée visuellement par le
           rendu serveur : on la relève avant l'échange, on la réapplique
           après. La requête correspondante suivra via la file. */
        const pendingIntents = [];
        document.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach((input) => {
          if (input.value === input.getAttribute('value')) return;
          /* On ne ré-applique que les intentions réellement en file : une
             saisie dont l'événement a été écrasé par le debounce unique de
             la liste (limite héritée de Dawn) ne partira jamais en
             requête — la ré-afficher figerait une valeur fausse. */
          const intentKey = input.dataset.lineKey || input.dataset.quantityVariantId || `line-${input.dataset.index}`;
          if (!CartItems.pendingActions.has(intentKey)) return;
          pendingIntents.push({ lineKey: input.dataset.lineKey || null, variantId: input.dataset.quantityVariantId, value: input.value });
        });

        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));

        pendingIntents.forEach((intent) => {
          const freshInput = intent.lineKey
            ? document.querySelector(`.quantity__input[data-line-key="${intent.lineKey}"]`)
            : document.querySelector(`.quantity__input[data-quantity-variant-id="${intent.variantId}"]`);
          if (freshInput) freshInput.value = intent.value;
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        /* Erreur réseau : restauration complète de l'état précédent. */
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
        if (quantity === 0) this.restoreRow(line);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartItems.changeInFlight = false;
        this.flushPendingActions();
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();

      this.addEventListener('change', debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER))
      }
  });
};

function handleDiscountForm(e) {
  e.preventDefault();

  const discountInput = e.target.querySelector('[name=cart-discount-field]');
  const discountError = e.target.querySelector('.cart-discount-form__error');
  const discountValue = discountInput.value;
  if (discountValue === undefined || discountValue.length === 0) {
    discountError.style.display = 'block';
    return;
  }
  discountError.style.display = 'none';
  const checkoutBaseUrl = '/checkout?discount=';
  const newCheckoutUrl = checkoutBaseUrl + discountValue;
  window.location.href = newCheckoutUrl;
}

function handleDiscountFormChange(e) {
  const discountErros = document.querySelectorAll('.cart-discount-form__error');
  discountErros.forEach(error => {
    error.style.display = 'none'
  })
}